export type PriceDisplayMode = "perPlayer" | "total";

export interface EventPricingFields {
  flightPricing?: boolean;
  eventFee?: number;
  earlyRegDiscount?: number;
  earlyRegCutoffDate?: Date | string | null;
  flighting?: {
    props?: { assignmentMode?: string };
    divisions?: Array<Record<string, unknown>>;
    flights?: Array<Record<string, unknown>>;
  };
}

export function isEarlyRegistrationActive(
  event: EventPricingFields,
  now: Date = new Date()
): boolean {
  if (!event?.earlyRegCutoffDate) return false;
  return now < new Date(event.earlyRegCutoffDate);
}

export function getEarlyRegDiscountAmount(
  event: EventPricingFields,
  now: Date = new Date()
): number {
  if (!isEarlyRegistrationActive(event, now)) return 0;
  const raw = Number(event.earlyRegDiscount);
  if (!raw) return 0;
  return Math.abs(raw);
}

export function applyEarlyRegDiscount(
  basePrice: number,
  event: EventPricingFields,
  now: Date = new Date()
): number {
  return basePrice - getEarlyRegDiscountAmount(event, now);
}

/** When event.flightPricing, compute min/max price from divisions and flights. */
export function getFlightPriceRange(
  event: EventPricingFields
): { min: number; max: number } | null {
  if (!event?.flightPricing || !event?.flighting) return null;
  const divisions = event.flighting.divisions ?? [];
  const flights = event.flighting.flights ?? [];
  const prices: number[] = [];
  divisions.forEach((d) => {
    if (typeof d.divisionPrice === "number") prices.push(d.divisionPrice as number);
  });
  flights.forEach((f) => {
    if (typeof f.flightPrice === "number") prices.push(f.flightPrice as number);
  });
  if (prices.length === 0) return null;
  return { min: Math.min(...prices), max: Math.max(...prices) };
}

/** Base price for one registration when flightPricing: flight, then division, else eventFee. */
export function getPriceForRegistration(
  event: EventPricingFields,
  player: Record<string, unknown>
): number {
  if (!event?.flightPricing) {
    return Number(event?.eventFee) || 0;
  }
  const divisions = event?.flighting?.divisions ?? [];
  const flights = event?.flighting?.flights ?? [];
  const flightVal = player.flight ?? player.flightName;
  const divisionVal = player.division ?? player.divisionName;
  if (flightVal != null && flightVal !== "" && flights.length > 0) {
    const f = flights.find(
      (x) => String(x.flightName ?? "") === String(flightVal)
    );
    if (typeof f?.flightPrice === "number") return f.flightPrice as number;
  }
  if (divisionVal != null && divisionVal !== "" && divisions.length > 0) {
    const d = divisions.find(
      (x) => String(x.divisionName ?? "") === String(divisionVal)
    );
    if (typeof d?.divisionPrice === "number") return d.divisionPrice as number;
  }
  return Number(event?.eventFee) || 0;
}

export function getRegistrationTotalPrice(
  event: EventPricingFields,
  player: Record<string, unknown>,
  now: Date = new Date()
): number {
  return applyEarlyRegDiscount(getPriceForRegistration(event, player), event, now);
}

function hasFlightPricingSelection(
  event: EventPricingFields,
  player: Record<string, unknown>
): boolean {
  const divisions = event?.flighting?.divisions ?? [];
  const flights = event?.flighting?.flights ?? [];
  const flightVal = player.flight ?? player.flightName;
  const divisionVal = player.division ?? player.divisionName;

  if (flightVal != null && flightVal !== "" && flights.length > 0) {
    const f = flights.find(
      (x) => String(x.flightName ?? "") === String(flightVal)
    );
    if (typeof f?.flightPrice === "number") return true;
  }
  if (divisionVal != null && divisionVal !== "" && divisions.length > 0) {
    const d = divisions.find(
      (x) => String(x.divisionName ?? "") === String(divisionVal)
    );
    if (typeof d?.divisionPrice === "number") return true;
  }
  return false;
}

export function scalePriceForDisplay(
  price: number,
  regQty: number,
  mode: PriceDisplayMode
): number {
  const qty = Math.max(1, Number(regQty) || 1);
  if (mode === "perPlayer" && qty > 1) {
    return Math.round((price / qty) * 100) / 100;
  }
  return price;
}

export function scalePriceRangeForDisplay(
  range: { min: number; max: number },
  regQty: number,
  mode: PriceDisplayMode
): { min: number; max: number } {
  return {
    min: scalePriceForDisplay(range.min, regQty, mode),
    max: scalePriceForDisplay(range.max, regQty, mode),
  };
}

export function formatDisplayPrice(amount: number): string {
  const rounded = Math.round(amount * 100) / 100;
  if (Number.isInteger(rounded)) return `$${rounded}`;
  return `$${rounded.toFixed(2)}`;
}

export function getPriceDisplaySuffix(mode: PriceDisplayMode): string {
  return mode === "perPlayer" ? "per player" : "total";
}

/** Whether the registration tab should show a resolved flight-pricing line. */
export function shouldShowResolvedRegistrationPrice(
  event: EventPricingFields,
  player: Record<string, unknown>
): boolean {
  if (!event?.flightPricing) return false;
  if (event.flighting?.props?.assignmentMode === "auto") return true;
  return hasFlightPricingSelection(event, player);
}
